import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Loader } from 'lucide-react';
import api from '../services/api';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);
    const [error, setError] = useState('');
    const [config, setConfig] = useState({
        orgLogoUrl: '',
        primaryAuthMethod: 'MANUAL', // Default fallback
        googleAuthConfigured: false,
        microsoftAuthConfigured: false,
        samlAuthConfigured: false,
        orgName: ''
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchSystemConfig();
        // Handle SSO redirects (success or error)
        const params = new URLSearchParams(window.location.search);
        
        // 1. Handle Success
        const authStatus = params.get('auth');
        const userData = params.get('user');
        
        if (authStatus === 'success' && userData) {
            try {
                const user = JSON.parse(decodeURIComponent(userData));
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('user', JSON.stringify(user));
                console.log('SSO Auth Success, redirecting to dashboard...');
                window.location.href = '/dashboard';
                return;
            } catch (err) {
                console.error('Failed to parse user data from SSO:', err);
                setError('Authentication succeeded but user data was malformed.');
            }
        }

        // 2. Handle Errors
        const errorParam = params.get('error');
        if (errorParam === 'saml_not_configured') {
            setError('SAML SSO is not configured yet. Please contact your administrator.');
        } else if (errorParam === 'auth_failed') {
            setError('SSO authentication failed. Please try again or use email/password.');
        } else if (errorParam === 'auth_error') {
            const message = params.get('message');
            setError(message || 'Authentication error occurred.');
        }
    }, [navigate]);

    const fetchSystemConfig = async () => {
        try {
            const response = await api.get('/auth/config');
            setConfig(response.data);
        } catch (err) {
            console.error('Failed to fetch system config', err);
        } finally {
            setConfigLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }

        setLoading(true);
        try {
            console.log('Attempting login for:', email);
            const response = await api.post('/auth/login', { email, password });
            console.log('Login response:', response.data);

            // Login Successful
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('user', JSON.stringify(response.data.user));

            console.log('Auth set in localStorage, navigating to dashboard...');

            // Use window.location for more reliable navigation in tests
            window.location.href = '/dashboard';
        } catch (err) {
            console.error('Login Failed:', err);
            setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            padding: '1rem'
        }}>
            <div style={{
                background: 'white',
                padding: '2.5rem',
                borderRadius: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                width: '100%',
                maxWidth: '400px',
                marginBottom: '1rem'
            }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                    <img src={config.orgLogoUrl || "/logo.png"} alt={config.orgName} style={{ height: '100px', objectFit: 'contain' }} />
                </div>
                <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: '#333' }}>{config.orgName} Login</h2>

                {error && (
                    <div style={{
                        background: '#fee2e2',
                        color: '#b91c1c',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        marginBottom: '1rem',
                        fontSize: '0.9rem',
                        textAlign: 'center'
                    }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleLogin}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>Email</label>
                        <div style={{ position: 'relative' }}>
                            <Mail size={20} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder="name@company.com"
                                required
                            />
                        </div>
                    </div>
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>Password</label>
                        <div style={{ position: 'relative' }}>
                            <Lock size={20} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder="Enter password"
                                required
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: loading ? '#93c5fd' : 'var(--primary, #4f46e5)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'background 0.2s',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        {loading && <Loader size={18} className="animate-spin" />}
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>

                    {/* Divider for SSO */}
                    {(config.googleAuthConfigured || config.microsoftAuthConfigured || config.samlAuthConfigured) && (
                        <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0' }}>
                            <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                            <span style={{ padding: '0 1rem', color: '#666', fontSize: '0.875rem' }}>OR</span>
                            <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                        </div>
                    )}

                    {/* Google Sign-In */}
                    {config.googleAuthConfigured && (
                        <button
                            type="button"
                            onClick={() => window.location.href = `${api.defaults.baseURL}/auth/google`}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'white',
                                color: '#444',
                                border: '1px solid #ddd',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginBottom: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18">
                                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
                                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
                                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.335z" />
                                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                            </svg>
                            Sign in with Google
                        </button>
                    )}

                    {/* Microsoft Sign-In */}
                    {config.microsoftAuthConfigured && (
                        <button
                            type="button"
                            onClick={() => window.location.href = `${api.defaults.baseURL}/auth/microsoft`}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'white',
                                color: '#444',
                                border: '1px solid #ddd',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                            <svg width="18" height="18" viewBox="0 0 23 23">
                                <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                                <path fill="#f35325" d="M1 1h10v10H1z" />
                                <path fill="#81bc06" d="M12 1h10v10H12z" />
                                <path fill="#05a6f0" d="M1 12h10v10H1z" />
                                <path fill="#ffba08" d="M12 12h10v10H12z" />
                            </svg>
                            Sign in with Microsoft
                        </button>
                    )}

                    {/* SAML SSO Sign-In */}
                    {config.samlAuthConfigured && (
                        <button
                            type="button"
                            onClick={() => window.location.href = `${api.defaults.baseURL}/auth/saml`}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'white',
                                color: '#444',
                                border: '1px solid #ddd',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginTop: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Login with SSO
                        </button>
                    )}

                    <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    </div>
                </form>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                PATHtoIP (C) 2026 | Version 1.1.0
            </p>
        </div>
    );
};

export default Login;
